import React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { pointsActionLabel } from "@shared/gamification";
import Link from "@/components/Link";
import { fetchPointsHistory, type PointsHistoryRow } from "@/api/userService";

// One row per ledger entry: date, action label, signed delta, reason, and a link to the source.
//
// Used on TWO surfaces with opposite chrome — the owner's «سجل النقاط» tab on /u/@id (Arabic RTL)
// and the admin Points-ledger dialog (English LTR). That is why `locale` is a PROP and the label
// tables are keyed off the ledger's action enum in `@shared/gamification` rather than inlined:
// two copies of the same table is how the surfaces drift apart.
//
// The endpoint behind this is refused with 403 to anyone who is not the owner or an admin, so
// there is no visibility decision to make here. If the caller renders this component, the server
// has already agreed.

const PAGE_SIZE = 50;

export type PointsHistoryListProps = {
  userId: string;
  locale?: "ar" | "en";
  // Review rows link to the store page when the caller knows the store. The ledger stores only a
  // review id, so without this the review link is omitted rather than guessed.
  storeHrefForReview?: (sourceId: string) => string | undefined;
};

const COPY = {
  ar: {
    empty: "لسه مافيش أي نقط في السجل.",
    error: "تعذّر تحميل سجل النقاط.",
    loading: "جارِ التحميل…",
    more: "عرض المزيد",
  },
  en: {
    empty: "No ledger entries yet.",
    error: "Could not load the points ledger.",
    loading: "Loading...",
    more: "Show more",
  },
};

const PointsHistoryList: React.FC<PointsHistoryListProps> = ({
  userId,
  locale = "ar",
  storeHrefForReview,
}) => {
  const isArabic = locale === "ar";
  const copy = COPY[locale];

  // Cursor pagination on `createdAt`, the same cursor the service takes. An infinite query rather
  // than page-accumulating state: pages must APPEND, and holding them in component state would
  // duplicate every page under StrictMode's double render.
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["pointsHistory", userId],
      initialPageParam: undefined as string | undefined,
      queryFn: ({ pageParam }) =>
        fetchPointsHistory({ userId, before: pageParam, limit: PAGE_SIZE }),
      getNextPageParam: (last: PointsHistoryRow[]) =>
        last.length === PAGE_SIZE ? last[last.length - 1]?.createdAt : undefined,
      enabled: !!userId,
      staleTime: 60 * 1000,
    });

  const rows: PointsHistoryRow[] = data?.pages.flat() ?? [];

  const sourceHref = (row: PointsHistoryRow): string | undefined => {
    if (!row.sourceId) return undefined;
    if (row.sourceType === "store") return `/store/${row.sourceId}`;
    if (row.sourceType === "review") return storeHrefForReview?.(row.sourceId);
    // `account` entries (signup, phone verified, profile completed) have nothing to link to.
    return undefined;
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(isArabic ? "ar-EG" : "en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (isLoading && rows.length === 0) {
    return (
      <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)" }}>
        {copy.loading}
      </p>
    );
  }

  if (isError && rows.length === 0) {
    return (
      <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)" }}>
        {copy.error}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p style={{ fontFamily: "var(--ys-font)", fontSize: 14, color: "var(--ys-ink-3)" }}>
        {copy.empty}
      </p>
    );
  }

  return (
    <div dir={isArabic ? "rtl" : "ltr"} style={{ fontFamily: "var(--ys-font)" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map((row) => {
          const href = sourceHref(row);
          const label = pointsActionLabel({ action: row.action, locale });
          return (
            <div
              key={row.id}
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 0",
                borderBottom: "1px solid var(--ys-rule)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: "var(--ys-ink)" }}>
                  {href ? (
                    <Link to={href} style={{ color: "inherit", textDecoration: "underline" }}>
                      {label}
                    </Link>
                  ) : (
                    label
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--ys-ink-3)", marginTop: 2 }}>
                  {formatDate(row.createdAt)}
                  {row.reason ? ` · ${row.reason}` : ""}
                </div>
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  // A reversal is the one row a reader must not skim past.
                  color: row.points < 0 ? "var(--ys-danger, #c0392b)" : "var(--ys-ink)",
                }}
              >
                {row.points > 0 ? `+${row.points}` : row.points}
              </div>
            </div>
          );
        })}
      </div>

      {hasNextPage && (
        <button
          type="button"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
          style={{
            marginTop: 12,
            background: "transparent",
            border: "1px solid var(--ys-rule)",
            borderRadius: "var(--ys-r)",
            padding: "8px 16px",
            minHeight: 36,
            fontFamily: "var(--ys-font)",
            fontSize: 14,
            color: "var(--ys-ink-2)",
            cursor: "pointer",
          }}
        >
          {isFetchingNextPage ? copy.loading : copy.more}
        </button>
      )}
    </div>
  );
};

export default PointsHistoryList;
